import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { DEFAULT_CHECKER_INTERVAL } from 'core/constants';
import { ApiStatus, NotificationPosition } from 'core/enums';
import { useNotificationContext } from 'core/states';
import { INotificationContent } from 'core/types';
import { isEmptyObject } from 'core/utils';

interface IProps<T> {
  fetchData: (requestPayload: any, signal: AbortSignal) => Promise<T>;
  queryKey: string;
  requestPayload: any;
  enabled: boolean;
  isFetchDataFinished: (data: T) => boolean;
  onSuccess: (data?: T) => void;
  options?: {
    success?: INotificationContent;
    loading?: INotificationContent;
    error?: INotificationContent;
    notificationPosition?: NotificationPosition;
  };
  onSettled?: (data?: T) => void;
  onError?: (data?: T) => void;
  isFetchDataFailed?: (data: T) => boolean;
  getFetchDataFailedErrorMessage?: (data: T) => string;
}

export const useQueryChecker = <T extends { success?: boolean; error?: string }>({
  fetchData,
  queryKey,
  requestPayload,
  enabled,
  isFetchDataFinished,
  onSuccess,
  options = {},
  onSettled = null,
  onError = null,
  isFetchDataFailed = null,
  getFetchDataFailedErrorMessage = (data: T) => data?.error || '',
}: IProps<T>) => {
  const [refetchInterval, setRefetchInterval] = useState(DEFAULT_CHECKER_INTERVAL);
  const {
    notification,
    clearNotification,
    showLoadingNotification,
    showSuccessNotification,
    showErrorNotification,
  } = useNotificationContext();

  useEffect(() => {
    if (enabled) {
      setRefetchInterval(DEFAULT_CHECKER_INTERVAL);
    }
  }, [enabled]);

  const notificationPosition = options?.notificationPosition || NotificationPosition.DRAWER;

  const handleOnSettled = (data?: T) => {
    onSettled?.(data);

    if (isFetchDataFailed && isFetchDataFailed(data)) {
      showErrorNotification(
        { ...options?.error, message: getFetchDataFailedErrorMessage?.(data) },
        notificationPosition
      );

      setRefetchInterval(null);
      onError && onError(data);

      return;
    }
  };

  const { isLoading, isError } = useQuery(
    [queryKey, requestPayload],
    ({ signal }) => fetchData(requestPayload, signal),
    {
      enabled,
      refetchInterval,
      onSuccess: (data) => {
        if (!data?.success) {
          showErrorNotification({ ...options?.error, message: data?.error }, notificationPosition);

          setRefetchInterval(null);
          onError && onError(data);

          return;
        }

        if (!isFetchDataFinished(data)) {
          return;
        }

        if (options?.success) {
          showSuccessNotification(options?.success, notificationPosition);
        } else {
          clearNotification();
        }

        setRefetchInterval(null);

        onSuccess(data);
      },
      onSettled: handleOnSettled,
      onError: (error?: AxiosError) => {
        showErrorNotification(options?.error, notificationPosition, error);

        setRefetchInterval(null);
        onError && onError();
      },
    }
  );

  useEffect(() => {
    if (isLoading && !refetchInterval) {
      setRefetchInterval(DEFAULT_CHECKER_INTERVAL);
    }

    if (isEmptyObject(options?.loading)) {
      return;
    }

    if (
      isLoading &&
      !isError &&
      ApiStatus.ERROR !== notification?.type &&
      ApiStatus.LOADING !== notification?.type
    ) {
      showLoadingNotification(options?.loading, notificationPosition);
    }
  }, [
    notificationPosition,
    notification?.type,
    isLoading,
    refetchInterval,
    options?.loading,
    isError,
    showLoadingNotification,
  ]);
};
